import { createContext, useContext, useRef } from 'react'
import { Outlet } from 'react-router-dom'

/**
 * CasesStateContext — a route-scoped "keep-alive" cache for the Cases list page.
 *
 * This is NOT app-wide state. It is mounted *above* the Cases route subtree
 * (`/cases` and `/cases/:id`) in App.jsx, so it stays mounted while the user
 * moves between the list and a case detail — but unmounts (and forgets
 * everything) the moment they leave the Cases section entirely.
 *
 * The Cases page fully unmounts when a case detail opens (sibling routes), which
 * would otherwise throw away its loaded pages, filters, search, and scroll
 * position. It stashes a snapshot here on unmount and rehydrates from it on
 * remount, so returning from a case restores exactly where the user was. Leaving
 * the section (→ Dashboard, etc.) unmounts this provider → the snapshot is
 * dropped → the next visit loads fresh. A hard refresh also resets (new JS
 * context), which is the intended boundary.
 *
 * The value is a plain ref (not state): writing to `snapshotRef.current` must not
 * re-render anyone — it's a side-channel the page reads only at mount time.
 */
const CasesStateContext = createContext(null)

export function CasesStateProvider() {
  const snapshotRef = useRef(null)
  return (
    <CasesStateContext.Provider value={snapshotRef}>
      <Outlet />
    </CasesStateContext.Provider>
  )
}

/**
 * Returns the shared snapshot ref (or null if rendered outside the provider, in
 * which case the page simply behaves as before — always a fresh load).
 */
// Co-located provider + hook (as in the design guide's AuthContext). The Fast
// Refresh rule wants component-only files; the hook export is a deliberate exception.
// eslint-disable-next-line react-refresh/only-export-components
export function useCasesSnapshot() {
  return useContext(CasesStateContext)
}
